"use client";

import { TextShimmer } from "@/components/text-shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BLUR_DATA_URL } from "@/config/site";
import { encodedHolidayParams } from "@/feature/holiday/search/lib/holiday-formatter-helper";
import { absoluteUrl } from "@/lib/utils";
import { priceFormatter } from "@/lib/price-formatter";
import {
  CalendarDays,
  ChevronsRight,
  MapPin,
  Palmtree,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import {
  IHolidaySearchQueryParams,
  IHolidaySearchResult,
} from "../../types/holiday-search-type";

interface HolidayListCardProps {
  holiday: IHolidaySearchResult;
  searchData: IHolidaySearchQueryParams;
}

function getImageUrl(path: string | null | undefined): string {
  if (!path) return "https://placehold.co/600x400/png";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return absoluteUrl(path, true);
}

function formatAudienceLabel(holidayFor?: string | null): string {
  if (!holidayFor) return "All travelers";
  const normalized = holidayFor.trim().toUpperCase();
  if (normalized === "BOTH" || normalized === "ALL") return "All travelers";
  if (normalized === "FAMILY") return "Family";
  if (normalized === "COUPLE") return "Couples";
  if (normalized === "SOLO") return "Solo";
  if (normalized === "GROUP") return "Group";
  return `${holidayFor.charAt(0).toUpperCase()}${holidayFor.slice(1).toLowerCase()}`;
}

export default function HolidayListCard({
  holiday,
  searchData,
}: HolidayListCardProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const holidayId = holiday.slug;
  const listedCities = holiday.cities ?? [];
  const cityChips = listedCities.slice(0, 4);
  const extraCityCount = Math.max(listedCities.length - cityChips.length, 0);

  const destinationText =
    listedCities
      .map((city) =>
        city.country ? `${city.city_name}, ${city.country}` : city.city_name,
      )
      .filter(Boolean)
      .join(" • ") ||
    searchData.city ||
    searchData.country ||
    null;

  const params = encodedHolidayParams(searchData);
  const detailsHref = params
    ? `/holiday/${encodeURIComponent(holidayId)}?${params}`
    : `/holiday/${encodeURIComponent(holidayId)}`;

  const parsedPrice = holiday.price ? Number(holiday.price) : NaN;
  const hasPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;

  const rawDiscount =
    holiday.discount_price ??
    (holiday.discount != null ? String(holiday.discount) : undefined);
  const parsedDiscount = rawDiscount ? Number(rawDiscount) : 0;
  const rawDiscountType = holiday.discount_type ?? "";
  const isPercentageDiscount =
    rawDiscountType.toUpperCase() === "PER" ||
    rawDiscountType.toUpperCase() === "PERCENT" ||
    rawDiscountType.toUpperCase() === "PERCENTAGE" ||
    rawDiscountType === "%";
  const hasDiscount = Number.isFinite(parsedDiscount) && parsedDiscount > 0;

  let originalPrice: number | null = null;
  if (hasPrice && hasDiscount) {
    if (isPercentageDiscount && parsedDiscount < 100) {
      originalPrice = Math.round(parsedPrice / (1 - parsedDiscount / 100));
    } else if (!isPercentageDiscount && parsedDiscount > 0) {
      originalPrice = parsedPrice + parsedDiscount;
    }
  }

  const dailyPrice =
    hasPrice && holiday.duration && holiday.duration > 0
      ? Math.round(parsedPrice / holiday.duration)
      : null;

  const audienceLabel = formatAudienceLabel(holiday.holiday_for);

  const handleViewDetails = () => {
    startTransition(() => {
      router.push(detailsHref);
    });
  };

  return (
    <div className="group w-full overflow-hidden rounded-2xl border border-border/70 bg-card p-0 shadow-xs transition-all duration-300 hover:border-primary/40 hover:shadow-md">
      <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] lg:grid-cols-[260px_minmax(0,1fr)_260px]">
        {/* 1. Left Image */}
        <div
          className="relative h-56 w-full cursor-pointer overflow-hidden bg-muted/40 md:h-full md:min-h-[220px]"
          onClick={handleViewDetails}
        >
          {holiday.image ? (
            <Image
              src={getImageUrl(holiday.image)}
              alt={holiday.title}
              fill
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, 260px"
              quality={75}
              placeholder="blur"
              blurDataURL={BLUR_DATA_URL}
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-muted to-muted/80 p-4 text-center">
              <Palmtree className="size-10 text-primary/40 mb-1" />
              <span className="text-xs text-muted-foreground font-medium line-clamp-1">
                {holiday.title}
              </span>
            </div>
          )}
        </div>

        {/* 2. Middle Content */}
        <div className="flex min-w-0 flex-col justify-between gap-3 p-4 md:p-5">
          <div className="space-y-2.5">
            {/* Title */}
            <div>
              <h2
                onClick={handleViewDetails}
                className="cursor-pointer line-clamp-2 text-lg md:text-xl font-bold leading-tight text-foreground transition-colors group-hover:text-primary"
              >
                {holiday.title}
              </h2>

              {/* Location */}
              {destinationText ? (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="size-3.5 shrink-0 text-rose-500" />
                  <span className="line-clamp-1">{destinationText}</span>
                </div>
              ) : null}
            </div>

            {/* Blue accent left-border box */}
            <div className="border-l-2 border-primary pl-2.5 py-0.5 space-y-0.5">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <CalendarDays className="size-4 text-primary shrink-0" />
                <span>
                  {holiday.duration
                    ? `${holiday.duration} Days Tour Package`
                    : "Holiday Package"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {audienceLabel}
                {listedCities.length > 0
                  ? ` • ${listedCities.length} ${listedCities.length === 1 ? "destination" : "destinations"} included`
                  : ""}
              </p>
            </div>

            {/* City / Service Pills */}
            {listedCities.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                {cityChips.map((city) => (
                  <Badge
                    key={`${holiday.id}-${city.city_id}`}
                    variant="secondary"
                    className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/15"
                  >
                    <MapPin className="size-3 mr-1 text-primary" />
                    {city.city_name}
                  </Badge>
                ))}
                {extraCityCount > 0 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-full px-2 py-0.5 text-[10px]"
                  >
                    +{extraCityCount}
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* 3. Right Pricing Column */}
        <div className="p-4 md:p-5 flex flex-col justify-between items-start lg:items-end lg:text-right gap-4">
          <div className="w-full space-y-1 lg:items-end flex flex-col">
            {/* Top Right Badge (Only if discount exists) */}
            {hasDiscount ? (
              <span className="inline-flex items-center rounded-md border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-600 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-400">
                {isPercentageDiscount
                  ? `${parsedDiscount}% OFF`
                  : `Save ${priceFormatter(parsedDiscount)}`}
              </span>
            ) : null}

            {/* Subtitle */}
            <p className="text-xs text-muted-foreground pt-0.5">
              {holiday.duration ? `${holiday.duration} days` : "Package"} • {audienceLabel}
            </p>

            {/* Main Price */}
            {hasPrice ? (
              <div className="space-y-0.5 pt-0.5">
                <div className="flex items-baseline gap-1 lg:justify-end">
                  <span className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
                    {priceFormatter(parsedPrice)}
                  </span>
                </div>

                {dailyPrice ? (
                  <p className="text-xs text-muted-foreground">
                    Approx. {priceFormatter(dailyPrice)} per day
                  </p>
                ) : null}

                {originalPrice && originalPrice > parsedPrice ? (
                  <p className="text-xs text-muted-foreground pt-0.5">
                    Base fare {priceFormatter(originalPrice)}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground pt-0.5">
                    All taxes & fees included
                  </p>
                )}
              </div>
            ) : (
              <div className="pt-2">
                <p className="text-lg font-bold text-primary">Price on request</p>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <div className="w-full lg:w-auto">
            <Button
              onClick={handleViewDetails}
              className="h-10 w-full sm:w-auto rounded-lg px-5 font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs flex items-center justify-center gap-1.5"
              disabled={isPending}
            >
              {isPending ? (
                <TextShimmer>See availability</TextShimmer>
              ) : (
                <>
                  <span>See availability</span>
                  <ChevronsRight className="size-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
